<template>
  <div
    class="builder-toolbar d-flex flex-column"
    :class="{ 'history-mode': canvasMode === 'history', 'stack-mode': viewMode === 'stack' }"
  >
    <!-- Canvas Mode Toggle Bar -->
    <div class="canvas-mode-toggle">
      <div class="d-flex">
        <button
          type="button"
          class="bb-btn-secondary-cta"
          :class="{ active: canvasMode === 'editor' }"
          @click="$emit('setCanvasMode', 'editor')"
        >
          <i class="bi bi-code-slash me-1"></i>Editor
        </button>
        <button
          type="button"
          class="bb-btn-secondary-cta"
          :class="{ active: canvasMode === 'history' }"
          @click="$emit('setCanvasMode', 'history')"
        >
          <i class="bi bi-clock-history me-1"></i>History
        </button>
      </div>
    </div>

    <!-- Controls (top-right corner, always shown — bumped down in history mode
         to clear the RunSummaryHeader that sits at the top of the canvas area).
         Hidden entirely in history+canvas mode: the RunCanvas mounted there owns
         the whole cluster (its toggle rides in RunCanvas's #controls-top slot), so
         a second toolbar cluster would double up and mis-overlap. -->
    <div v-if="!hideControls" class="zoom-controls position-absolute d-flex flex-column gap-1">
      <!-- View Mode Toggle -->
      <button
        @click="$emit('toggleViewMode')"
        class="btn bb-btn-vueflow-ctrl d-flex align-items-center justify-content-center"
        :title="viewMode === 'canvas' ? 'Switch to List View' : 'Switch to Canvas View'"
      >
        <i :class="viewMode === 'canvas' ? 'bi bi-list-ul' : 'bi bi-diagram-3'"></i>
      </button>

      <span class="border-bottom my-1"></span>

      <!-- Canvas-specific controls (shared with the read-only RunCanvas). -->
      <CanvasControls
        v-if="viewMode === 'canvas'"
        :flow-direction="flowDirection"
        @zoom-in="$emit('zoomIn')"
        @zoom-out="$emit('zoomOut')"
        @fit-view="$emit('fitView')"
        @apply-hierarchical-layout="$emit('applyHierarchicalLayout')"
        @toggle-flow-direction="$emit('toggleFlowDirection')"
      />

      <!-- Stack-specific controls -->
      <template v-else-if="viewMode === 'stack'">
        <button
          @click="$emit('expandAll')"
          class="btn bb-btn-vueflow-ctrl d-flex align-items-center justify-content-center"
          title="Expand All Services"
        >
          <i class="bi bi-chevron-bar-down"></i>
        </button>
        <button
          @click="$emit('collapseAll')"
          class="btn bb-btn-vueflow-ctrl d-flex align-items-center justify-content-center"
          title="Collapse All Services"
        >
          <i class="bi bi-chevron-bar-up"></i>
        </button>
        <button
          type="button"
          @click="$emit('toggleFlatten')"
          class="btn bb-btn-vueflow-ctrl d-flex align-items-center justify-content-center"
          :class="{ active: flat }"
          :title="flat ? 'Restore indentation' : 'Flatten to full width'"
        >
          <i :class="flat ? 'bi bi-text-indent-right' : 'bi bi-text-indent-left'"></i>
        </button>
      </template>
    </div>

    <!-- Floating Run Workflow Button (editor only — running runs are viewed in history) -->
    <div v-if="canvasMode === 'editor'" class="floating-run-button align-self-center">
      <div class="d-flex gap-2 flex-nowrap">
        <button
          class="bb-btn-secondary-cta"
          :title="hasDraftChanges ? 'Apply your unpublished changes and run' : 'Run Workflow'"
          @click="$emit('executeWorkflow')"
        >
          <i class="bi bi-play-fill me-2"></i>{{ hasDraftChanges ? 'Apply &amp; Run' : 'Run Workflow' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import CanvasControls from "@/views/runhistory/CanvasControls.vue";

defineProps<{
  canvasMode: string;
  viewMode: string;
  flowDirection: string;
  // History+canvas mode mounts a RunCanvas that owns the whole floating cluster (toggle +
  // canvas controls); hide the toolbar's cluster entirely there so the two don't double up.
  hideControls?: boolean;
  // Stacked view: whether the indentation is currently flattened (drives the toggle's active state).
  flat?: boolean;
  // Drives the Run button's "Apply & Run" label when the open workflow has unapplied edits.
  hasDraftChanges?: boolean;
}>();

defineEmits<{
  setCanvasMode: [mode: "editor" | "history"];
  toggleViewMode: [];
  expandAll: [];
  collapseAll: [];
  toggleFlatten: [];
  zoomIn: [];
  zoomOut: [];
  fitView: [];
  applyHierarchicalLayout: [];
  toggleFlowDirection: [];
  executeWorkflow: [];
}>();
</script>

<style scoped lang="scss">
/* Main toolbar - fills canvas area, transparent to clicks */
.builder-toolbar {
  position: absolute;
  inset: 0;
  z-index: 5;
  pointer-events: none;
}

/* Re-enable pointer events on interactive elements */
.canvas-mode-toggle,
.floating-run-button,
.zoom-controls {
  pointer-events: auto;
}

/* Controls panel - top-right corner */
.zoom-controls {
  top: 1.65em;
  right: 1.3em;
}

/* Editor + stack: line the toggle button up with the first service panel. The panel top is
   0.75rem (.builder-canvas-inner.editor-stack padding — the breathing room that replaces the
   RunSummaryHeader the other surfaces have) + 1rem (.service-stack padding-top) = 1.75rem. The
   canvas view keeps the 1.65em inset above. Scoped to :not(.history-mode) so history+stack uses
   the calc rule below, which accounts for the RunSummaryHeader sitting above the stack. */
.builder-toolbar.stack-mode:not(.history-mode) .zoom-controls {
  top: 1.75rem;
}

/* In history mode the RunSummaryHeader sits at the top of .builder-canvas. Its height varies
   (with/without error), so BuilderView measures the actual top of .builder-canvas-inner and
   exposes it as --zoom-controls-top on the wrapper. The +1rem matches .service-stack's 1rem
   padding-top, so the toggle lines up with the first service panel in history+stack. (Canvas
   mode here hides this cluster via hideControls — RunCanvas owns its own controls.) */
.builder-toolbar.history-mode .zoom-controls {
  top: calc(var(--zoom-controls-top, 7em) + 1rem);
}


/* Canvas Mode Toggle Bar */
.canvas-mode-toggle {
  position: absolute;
  top: -30px;
  left: 50%;
  transform: translateX(-50%);
}

.canvas-mode-toggle .d-flex {
  background-color: var(--bs-body-bg);
  border-radius: var(--bs-border-radius);
  padding: 2px;
}

.canvas-mode-toggle .d-flex button {
  padding: 4px 12px;
}

.canvas-mode-toggle .d-flex button:first-child {
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
}

.canvas-mode-toggle .d-flex button:last-child {
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
  margin-left: -1px;
}

/* Floating Run Button - fixed to viewport bottom, centered via flex align-self */
.floating-run-button {
  position: fixed;
  bottom: 20px;
  background-color: var(--bs-body-bg);
}

@media (max-width: 768px) {
  .canvas-mode-toggle {
    top: -10px !important;
  }

  /* History mode only: lift the toggle higher so it clears the run summary box. */
  .builder-toolbar.history-mode .canvas-mode-toggle {
    top: -40px !important;
  }

  /* Match the tightened stack spacing: 0.5rem edge gap for the controls, tops aligned
     with the first service panel at the mobile paddings (see .service-stack mobile). */
  .zoom-controls {
    right: 0.5rem;
  }

  .builder-toolbar.stack-mode:not(.history-mode) .zoom-controls {
    top: 1.125rem;
  }

  .builder-toolbar.history-mode .zoom-controls {
    top: calc(var(--zoom-controls-top, 7em) + 0.375rem);
  }
}
</style>
